<template>
  <v-container fluid class="active-call-view pa-0">
    <!-- Patient declined overlay -->
    <div v-if="declinedByPatient" class="declined-overlay">
      <div class="declined-card">
        <v-icon size="64" color="error" class="mb-4">mdi-phone-cancel</v-icon>
        <p class="declined-title">Patient declined your call</p>
        <p class="declined-subtitle">Returning to room list…</p>
        <v-progress-linear indeterminate color="error" rounded class="mt-6" height="4" />
      </div>
    </div>

    <!-- Audio enable prompt (page refresh scenario) -->
    <div v-if="needsAudioEnable && !declinedByPatient" class="audio-enable-overlay">
      <div class="audio-enable-card">
        <v-icon size="64" color="primary" class="mb-4">mdi-volume-high</v-icon>
        <p class="audio-enable-title">Enable Audio</p>
        <p class="audio-enable-subtitle">Audio was paused when the page refreshed. Click below to enable audio.</p>
        <v-btn color="primary" size="large" @click="handleEnableAudio" class="mt-4">
          <v-icon start>mdi-volume-high</v-icon>
          Enable Audio
        </v-btn>
      </div>
    </div>

    <v-row v-else no-gutters class="fill-height">
      <v-col cols="12" class="remote-video-container">
        <video-display :stream="remoteStream" label="Patient Room" />

        <v-chip class="connection-status" :color="statusColor" variant="flat">
          <v-icon start>{{ statusIcon }}</v-icon>
          {{ statusText }}
        </v-chip>

        <div class="local-video-pip">
          <video-display :stream="localStream" label="You" :mirrored="true" :muted="true" />
        </div>

        <div class="controls-overlay">
          <div class="call-duration-display">{{ callDuration }}</div>
          <call-controls
            :is-audio-enabled="callStore.isAudioEnabled"
            :is-video-enabled="callStore.isVideoEnabled"
            @toggle-audio="toggleAudio"
            @toggle-video="toggleVideo"
            @end-call="handleEndCall"
          />
          <!-- Recording props/handlers hidden per user request -->
          <!--
            :is-recording="isRecording"
            :recording-status="recordingStatus"
            @start-recording="handleStartRecording"
            @stop-recording="handleStopRecording"
          -->
        </div>
      </v-col>
    </v-row>

    <v-dialog v-model="showError" max-width="400">
      <v-card>
        <v-card-title>Call Error</v-card-title>
        <v-card-text>{{ error }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="primary" @click="handleEndCall">End Call</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Notes modal -->
    <notes-modal
      v-model="showNotesModal"
      :call-was-accepted="callWasAccepted"
      :session-id="sessionId"
      :patient-name="patientName"
      @notes-saved="handleNotesSaved"
      @notes-skipped="handleNotesSkipped"
    />
  </v-container>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import { useRouter } from "vue-router";
import { useCallStore } from "../store/callStore";
import { usePatientStore } from "../store/patientStore";
import {
  useVideoClient,
  getZoomClientInstance,
  type SessionCredentials,
  type VideoProviderType,
} from "@virtual-care/shared";
import { callApiClient } from "../services/callApiClient";
import { signalingService } from "../services/signalingService";
import { callLogService } from "../services/callLogService";
import VideoDisplay from "../components/VideoDisplay.vue";
import CallControls from "../components/CallControls.vue";
import NotesModal from "../components/NotesModal.vue";

interface Props {
  sessionId: string;
}

const props = defineProps<Props>();
const router = useRouter();
const callStore = useCallStore();
const patientStore = usePatientStore();

// Derive patientId from roomId stored in callStore (roomId alone is not PHI).
// Must be called BEFORE callStore.endCall() which nulls out roomId.
const refreshCallLog = () => {
  const roomId = callStore.roomId;
  if (!roomId) return;
  const patient = patientStore.patients.find((p) => p.room.roomId === roomId);
  if (patient) {
    patientStore.refreshPatientCallLog(patient.patientId).catch(() => {});
  }
};

const userId = "nurse-001";

// From POST /sessions/:id/join — stored in callStore during confirmCall (usePatientActions).
// If the route is opened directly (e.g. micro-frontend) or after refresh, store may be empty;
// beginCall() then POSTs join to fetch credentials before startCall.
const sessionCredentials = (callStore.credentials ?? {}) as SessionCredentials;
const providerType = (callStore.provider ?? "zoom") as VideoProviderType;

const {
  localStream,
  remoteStream,
  isConnected,
  error: webrtcError,
  startCall,
  endCall,
  toggleAudio: toggleAudioTrack,
  toggleVideo: toggleVideoTrack,
} = useVideoClient(props.sessionId, userId, "nurse", providerType, sessionCredentials, signalingService);

const showError = ref(false);
const isEnding = ref(false);
const declinedByPatient = ref(false);
const startTime = ref<number | null>(null);
const callDuration = ref("00:00");
let durationInterval: ReturnType<typeof setInterval> | null = null;

// Recording state (hidden per user request)
const isRecording = ref(false);
const recordingStatus = ref<"idle" | "recording" | "paused">("idle");

// Audio enable state (for page refresh scenarios)
const needsAudioEnable = ref(false);
const wasRestoredFromRefresh = ref(false);

// Notes modal state
const showNotesModal = ref(false);
const callWasAccepted = ref(false);
const patientName = ref('');
const sessionEndedRemotely = ref(false);

const statusColor = computed(() => {
  switch (callStore.status) {
    case "connecting":
      return "warning";
    case "connected":
      return "success";
    case "failed":
      return "error";
    default:
      return "info";
  }
});

const statusIcon = computed(() => {
  switch (callStore.status) {
    case "connecting":
      return "mdi-loading mdi-spin";
    case "connected":
      return "mdi-check-circle";
    case "failed":
      return "mdi-alert-circle";
    default:
      return "mdi-information";
  }
});

const statusText = computed(() => {
  switch (callStore.status) {
    case "initiating":
      return "Initiating call...";
    case "connecting":
      return "Connecting...";
    case "connected":
      return "Connected";
    case "failed":
      return "Connection failed";
    default:
      return "Unknown";
  }
});

const error = computed(() => webrtcError.value || callStore.error?.message);

function cleanup() {
  if (durationInterval) {
    clearInterval(durationInterval);
    durationInterval = null;
  }
}

function startDurationTimer() {
  startTime.value = Date.now();
  durationInterval = setInterval(() => {
    if (startTime.value) {
      const elapsed = Math.floor((Date.now() - startTime.value) / 1000);
      const m = Math.floor(elapsed / 60)
        .toString()
        .padStart(2, "0");
      const s = (elapsed % 60).toString().padStart(2, "0");
      callDuration.value = `${m}:${s}`;
    }
  }, 1000);
}

const onCallEnded = async () => {
  if (isEnding.value) return; // Already handling end from our side
  isEnding.value = true;
  
  console.log('[ActiveCallView] Patient ended call - ending video and showing notes modal');
  sessionEndedRemotely.value = true; // Mark that remote party ended it
  
  // Get patient info and call status BEFORE cleanup
  const patient = patientStore.patients.find(p => p.room.roomId === callStore.roomId);
  patientName.value = patient?.patientName || 'Patient';
  
  const callLog = await callLogService.getCallLogBySessionId(props.sessionId);
  callWasAccepted.value = callLog?.status === 'accepted' || callLog?.status === 'completed';
  
  // End the video call immediately to save Zoom minutes
  cleanup();
  await endCall();
  
  // Refresh call log
  refreshCallLog();
  callStore.endCall();
  
  // NOW show notes modal (video call already ended)
  showNotesModal.value = true;
};

const onPatientDeclined = async () => {
  if (isEnding.value) return;
  isEnding.value = true;
  cleanup();
  refreshCallLog(); // capture roomId before endCall() nulls it
  await endCall();
  callStore.endCall();
  declinedByPatient.value = true;
  setTimeout(() => router.push("/nurse-console"), 3000);
};

// Recording functionality disabled (hidden per user request)
const setupRecordingListeners = async () => {
  // Set up recording event listeners (only for Zoom provider)
  if (providerType === "zoom") {
    try {
      const zoomClient = getZoomClientInstance();

      zoomClient.on("recording-started", () => {
        console.log("[ActiveCallView] Recording started");
        isRecording.value = true;
        recordingStatus.value = "recording";
      });

      zoomClient.on("recording-stopped", () => {
        console.log("[ActiveCallView] Recording stopped");
        isRecording.value = false;
        recordingStatus.value = "idle";
      });

      zoomClient.on("recording-paused", () => {
        console.log("[ActiveCallView] Recording paused");
        recordingStatus.value = "paused";
      });

      zoomClient.on("recording-resumed", () => {
        console.log("[ActiveCallView] Recording resumed");
        recordingStatus.value = "recording";
      });
    } catch (err) {
      console.warn("[ActiveCallView] Failed to set up recording listeners:", err);
    }
  }
};


/** Ensure Zoom JWT + display name exist: use store if set, else POST /sessions/:id/join */
async function ensureSessionCredentials(): Promise<SessionCredentials> {
  const existing = callStore.credentials as SessionCredentials | undefined;
  if (existing?.token && typeof existing.token === "string" && existing.token.trim().length > 0) {
    return {
      ...existing,
      userName:
        typeof existing.userName === "string" && existing.userName.trim().length > 0 ? existing.userName : userId,
    };
  }

  // Credentials not in store - fetching from API (page refresh scenario)
  wasRestoredFromRefresh.value = true;
  
  const joinResponse = await callApiClient.joinSession(props.sessionId, "nurse");
  const session = joinResponse.session;
  const creds = session?.credentials as SessionCredentials | undefined;
  if (!creds?.token || typeof creds.token !== "string" || creds.token.trim().length === 0) {
    throw new Error("Missing session token from join response");
  }

  callStore.$patch({
    credentials: creds,
    ...(session?.roomId != null && session.roomId !== "" ? { roomId: session.roomId } : {}),
    ...(session?.provider != null ? { provider: session.provider as VideoProviderType } : {}),
  });

  return creds;
}

const beginCall = async () => {
  try {
    callStore.setStatus("connecting");
    const creds = await ensureSessionCredentials();
    await startCall(creds);
    callStore.setStatus("connected");
    startDurationTimer(); // Start call duration timer
    // await setupRecordingListeners(); // Recording disabled (hidden per user request)
    
    // If session was restored from refresh, prompt for audio enable
    if (wasRestoredFromRefresh.value) {
      needsAudioEnable.value = true;
    }
  } catch (err) {
    console.error("[ActiveCallView] Failed to start call:", err);
    callStore.setError("CALL_FAILED", "Failed to establish connection");
    showError.value = true;
  }
};

onMounted(async () => {
  signalingService.joinSession(props.sessionId);
  signalingService.on("call_ended", onCallEnded);
  signalingService.on("call_declined", onPatientDeclined);
  await beginCall();
});

onBeforeUnmount(() => {
  signalingService.off("call_ended", onCallEnded);
  signalingService.off("call_declined", onPatientDeclined);
  cleanup();
  if (!isEnding.value) {
    endCall();
  }
});

const toggleAudio = () => {
  toggleAudioTrack();
  callStore.toggleAudio();
};

const toggleVideo = () => {
  toggleVideoTrack();
  callStore.toggleVideo();
};

// Recording handlers disabled (hidden per user request)
const handleStartRecording = async () => {
  if (providerType !== "zoom") {
    console.warn("[ActiveCallView] Recording only supported for Zoom provider");
    return;
  }

  try {
    const zoomClient = getZoomClientInstance();
    await zoomClient.startRecording();
    console.log("[ActiveCallView] Recording start requested");
  } catch (err) {
    console.error("[ActiveCallView] Failed to start recording:", err);
    const errorMessage = err instanceof Error ? err.message : "Failed to start recording";
    callStore.setError("RECORDING_FAILED", errorMessage);
    showError.value = true;
  }
};

const handleStopRecording = async () => {
  if (providerType !== "zoom") {
    return;
  }

  try {
    const zoomClient = getZoomClientInstance();
    await zoomClient.stopRecording();
    console.log("[ActiveCallView] Recording stop requested");
  } catch (err) {
    console.error("[ActiveCallView] Failed to stop recording:", err);
    const errorMessage = err instanceof Error ? err.message : "Failed to stop recording";
    callStore.setError("RECORDING_FAILED", errorMessage);
    showError.value = true;
  }
};


const handleEndCall = async () => {
  if (isEnding.value) return;
  isEnding.value = true;
  
  // Get patient info and call status BEFORE ending call (while we still have roomId)
  const patient = patientStore.patients.find(p => p.room.roomId === callStore.roomId);
  patientName.value = patient?.patientName || 'Patient';
  
  const callLog = await callLogService.getCallLogBySessionId(props.sessionId);
  callWasAccepted.value = callLog?.status === 'accepted' || callLog?.status === 'completed';
  
  // End the video call immediately to save Zoom minutes
  cleanup();
  await endCall();
  
  // End the backend session
  try {
    await callApiClient.endSession(props.sessionId);
    signalingService.notifyCallEnded(props.sessionId, userId);
  } catch (err) {
    console.warn('[ActiveCallView] Session already ended:', err);
  }
  
  // Refresh call log AFTER ending session
  refreshCallLog();
  callStore.endCall();
  
  // NOW show notes modal (video call already ended)
  showNotesModal.value = true;
};

const handleNotesSaved = async (notes: string) => {
  // Update call log with notes
  const callLog = await callLogService.getCallLogBySessionId(props.sessionId);
  if (callLog) {
    await callLogService.updateCallLog(callLog.callId, {
      notes,
      notesEditedAt: new Date().toISOString(),
      notesEditedBy: userId,
    });
  }
  
  // Navigate away (call already ended)
  showNotesModal.value = false;
  router.push("/nurse-console");
};

const handleNotesSkipped = async () => {
  // Navigate away without notes (call already ended)
  showNotesModal.value = false;
  router.push("/nurse-console");
};

// completeEndCall is no longer needed - keeping for backwards compatibility
const completeEndCall = async () => {
  showNotesModal.value = false;
  router.push("/nurse-console");
};

async function handleEnableAudio() {
  try {
    console.log("[ActiveCallView] Nurse clicked Enable Audio button");
    const zoomClient = getZoomClientInstance();
    await zoomClient.unmuteAudio();
    console.log("[ActiveCallView] Audio started successfully");
    needsAudioEnable.value = false;
  } catch (err) {
    console.error("[ActiveCallView] Failed to enable audio:", err);
    callStore.setError("AUDIO_FAILED", "Failed to enable audio. Please try again.");
    showError.value = true;
  }
}
</script>

<style lang="scss" scoped>
.active-call-view {
  height: 100vh;
  background: #000;
}

.declined-overlay {
  height: 100vh;
  width: 100%;
  background: #1a1a1a;
  display: flex;
  align-items: center;
  justify-content: center;
}

.declined-card {
  text-align: center;
  padding: 48px;
  max-width: 380px;
}

.declined-title {
  font-size: 26px;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 8px;
}

.declined-subtitle {
  font-size: 18px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.55);
  margin: 0;
}

.audio-enable-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  height: 100vh;
  width: 100%;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(8px);
}

.audio-enable-card {
  text-align: center;
  padding: 48px;
  max-width: 420px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  border: 1px solid rgba(121, 93, 233, 0.3);
}

.audio-enable-title {
  font-size: 28px;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 12px;
}

.audio-enable-subtitle {
  font-size: 16px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.7);
  margin: 0;
  line-height: 1.5;
}

.fill-height {
  height: 100%;
}

.remote-video-container {
  position: relative;
  height: 100vh;
}

.connection-status {
  position: absolute;
  top: 20px;
  left: 20px;
  z-index: 10;
}

.local-video-pip {
  position: absolute;
  bottom: 100px;
  right: 20px;

  // Responsive sizing with clamp() - maintains 16:9 aspect ratio
  width: clamp(200px, 20vw, 320px);
  aspect-ratio: 16 / 9;

  border: 2px solid #795de9;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
  z-index: 10;
}

.controls-overlay {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;

  // Reserved space for controls (don't overlap with PiP)
  min-height: 60px;

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.call-duration-display {
  font-size: 18px;
  font-weight: 600;
  color: #ffffff;
  background: rgba(0, 0, 0, 0.5);
  padding: 6px 16px;
  border-radius: 20px;
  backdrop-filter: blur(8px);
}

// Small screen adjustments
@media (max-height: 600px) {
  .local-video-pip {
    width: clamp(150px, 15vw, 200px);
    bottom: 80px;
  }

  .controls-overlay {
    bottom: 10px;
  }
}
</style>
